import React, { FC, PropsWithChildren } from 'react';

import { graphql } from 'gatsby';
import { Box, Heading, Text, lucidParser, Image } from '@lucid/components-react';
import reactHTMLParser from 'html-react-parser';
import { Drupal_ParagraphFlexiSocialProof } from 'generated/drupal-graphql';
import { getProcessedValue } from '@lucid/utils';
import { mqMin } from '@lucid/theme';

import type { Lucid } from 'types/types';

interface FlexiSocialProofProps {
  entityId?: string;
  entity: Drupal_ParagraphFlexiSocialProof;
}

const FlexiSocialProof: FC<PropsWithChildren<FlexiSocialProofProps>> = ({
  entityId,
  entity: { fieldFlexiSocialProofBlock },
}) => (
  <Box
    key={entityId}
    css={(theme: Lucid.Theme) => ({
      label: 'flexSocialProof',
      display: 'flex',
      flexDirection: 'column',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      width: '100%',
      marginTop: theme.space[6],
      marginBottom: theme.space[6],
      [mqMin.tabletLandscape]: {
        flexDirection: 'row',
      },
    })}
    gridColumn="1 / -1"
  >
    {fieldFlexiSocialProofBlock &&
      fieldFlexiSocialProofBlock.map(({ entity }: any) => {
        const internalComponent = () => {
          if (entity.entityBundle === 'seo_image') {
            const { fieldAltText, fieldImageLink } = entity;
            return (
              <Image key={fieldImageLink} imageAlt={fieldAltText || ''} imageSrc={fieldImageLink} loading="lazy" />
            );
          } else {
            const { fieldFlexiBody, fieldFlexiHeader, fieldFlexiImage } = entity;
            return (
              <Box key={fieldFlexiHeader} css={{ textAlign: 'center' }}>
                <Image imageAlt="" imageSrc={fieldFlexiImage} loading="lazy" />
                <Heading
                  as="h3"
                  css={(theme: Lucid.Theme) => ({
                    color: theme.colors.blue[60],
                  })}
                  variant="headline3XL"
                >
                  {fieldFlexiHeader}
                </Heading>
                <Text as="div" variant="paragraphL300">
                  {reactHTMLParser(getProcessedValue(fieldFlexiBody), {
                    replace: lucidParser.link,
                  })}
                </Text>
              </Box>
            );
          }
        };

        return (
          <Box
            key={entity.entityId}
            css={{
              label: 'text',
              display: 'flex',
              flexDirection: 'column',
              [mqMin.tabletLandscape]: {
                flex: '0 1 30%',
                maxWidth: '30%',
              },
            }}
          >
            {internalComponent()}
          </Box>
        );
      })}
  </Box>
);

export default FlexiSocialProof;

export const query = graphql`
  fragment seoImage on Drupal_ParagraphSeoImage {
    fieldAltText
    fieldImageLink
  }
  fragment flexiSocialProof on Drupal_ParagraphFlexiSocialProof {
    entityId
    fieldFlexiSocialProofBlock {
      entity {
        entityId
        entityBundle
        ...seoImage
        ...flexiSection
      }
    }
  }
`;
